import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  Filter as FilterIcon,
  Handshake,
  Building2,
  ShieldAlert,
  Power,
  Trash2,
  Search,
  FileDown,
  Loader2,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import DataTable from '../../../components/DataTable';
import Pagination from '../../../components/Pagination';
import DynamicFormModal from '../../../components/DynamicFormModal';
import FilterDrawer from '../../../components/FilterDrawer';
import AppliedFilters from '../../../components/AppliedFilters';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { useToast } from '../../../components/ui/toaster.jsx';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import {
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  toggleSupplierStatus,
} from './Services/suppliersService.js';
import ExportExcelButton from '../../../components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const SORTABLE = new Set(['nombre', 'tipo', 'estado', 'updatedAt']);

const proveedorTypeOptions = [
  { value: 'Aerolíneas', label: 'Aerolíneas' },
  { value: 'Navieras', label: 'Navieras' },
  { value: 'Transportadores', label: 'Transportadores' },
  { value: 'Agentes de carga', label: 'Agentes de carga' },
  { value: 'Agentes de aduana', label: 'Agentes de aduana' },
  { value: 'Bodegas', label: 'Bodegas' },
];

const identificationTypeOptions = [
  { value: 'NIT', label: 'NIT - Número de Identificación Tributaria' },
  { value: 'CC', label: 'CC - Cédula de Ciudadanía' },
  { value: 'CE', label: 'CE - Cédula de Extranjería' },
  { value: 'PAS', label: 'PAS - Pasaporte' },
  { value: 'TAX_ID', label: 'TAX_ID - Tax ID' },
  { value: 'EIN', label: 'EIN - Employer Identification Number' },
  { value: 'VAT', label: 'VAT - VAT Number' },
  { value: 'RFC', label: 'RFC - Registro Federal de Contribuyentes' },
  { value: 'RUC', label: 'RUC - Registro Único de Contribuyentes' },
  { value: 'RUT', label: 'RUT - Rol/Registro Único Tributario' },
  { value: 'CUIT', label: 'CUIT - Clave Única de Identificación Tributaria' },
  { value: 'CNPJ', label: 'CNPJ - Cadastro Nacional da Pessoa Jurídica' },
  { value: 'OTRO', label: 'OTRO - Otro' },
];

const contactTypeOptions = [
  { value: 'Comercial', label: 'Comercial' },
  { value: 'Operaciones', label: 'Operaciones' },
  { value: 'Facturación', label: 'Facturación' },
  { value: 'Cartera', label: 'Cartera' },
  { value: 'Otro', label: 'Otro' },
];

const emptyContact = () => ({ nombre: '', cargo: '', tipoContacto: 'Comercial', correo: '', telefono: '', celular: '' });

const formFields = [
  { id: 'nombre', label: 'Razón social', type: 'text', required: true, placeholder: 'Ej. Transportes del Norte' },
  { id: 'nombreComercial', label: 'Nombre comercial', type: 'text', required: false, placeholder: 'Ej. TDN Logistics' },
  { id: 'identificacionTipo', label: 'Tipo de identificación', type: 'select', required: true, defaultValue: 'NIT', options: identificationTypeOptions },
  { id: 'identificacionNumero', label: 'Número de identificación', type: 'text', required: true, placeholder: '900123456' },
  { id: 'pais', label: 'País', type: 'text', required: true, placeholder: 'Chile' },
  { id: 'ciudad', label: 'Ciudad', type: 'text', required: true, placeholder: 'Santiago' },
  { id: 'direccion', label: 'Dirección', type: 'text', required: false, placeholder: 'Av. Principal 123' },
  { id: 'tiposProveedor', label: 'Tipos de proveedor', type: 'custom', defaultValue: [], render: (formData, setFormData) => {
    const selected = Array.isArray(formData.tiposProveedor) ? formData.tiposProveedor : [];
    return (
      <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
        <div className="text-sm text-muted-foreground">Selecciona uno o varios tipos de proveedor.</div>
        <div className="flex flex-wrap gap-2">
          {proveedorTypeOptions.map((option) => {
            const checked = selected.includes(option.value);
            return (
              <label key={option.value} className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((value) => value !== option.value)
                      : [...selected, option.value];
                    setFormData((prev) => ({ ...prev, tiposProveedor: next }));
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  } },
  { id: 'contactos', label: 'Contactos', type: 'custom', defaultValue: [], render: (formData, setFormData) => {
    const contacts = Array.isArray(formData.contactos) ? formData.contactos : [];
    const updateContact = (index, field, value) => {
      const next = contacts.map((contact, contactIndex) => contactIndex === index ? { ...contact, [field]: value } : contact);
      setFormData((prev) => ({ ...prev, contactos: next }));
    };
    const addContact = () => {
      setFormData((prev) => ({ ...prev, contactos: [...(Array.isArray(prev.contactos) ? prev.contactos : []), emptyContact()] }));
    };
    const removeContact = (index) => {
      const next = contacts.filter((_, contactIndex) => contactIndex !== index);
      setFormData((prev) => ({ ...prev, contactos: next }));
    };
    return (
      <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Agrega uno o varios contactos para este proveedor.</div>
          <Button type="button" variant="outline" size="sm" onClick={addContact}>
            <PlusCircle className="mr-2 h-4 w-4" /> Añadir contacto
          </Button>
        </div>
        {contacts.length === 0 ? (
          <div className="rounded-md border border-border/70 bg-muted/30 p-3 text-sm text-muted-foreground">Aún no hay contactos agregados.</div>
        ) : (
          contacts.map((contact, index) => (
            <div key={`contact-${index}`} className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">Contacto {index + 1}</div>
                <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(index)} aria-label="Eliminar contacto">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-sm">Nombre *</Label>
                  <Input value={contact.nombre || ''} onChange={(e) => updateContact(index, 'nombre', e.target.value)} placeholder="Nombre del contacto" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Cargo</Label>
                  <Input value={contact.cargo || ''} onChange={(e) => updateContact(index, 'cargo', e.target.value)} placeholder="Ej. Supervisor" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Tipo de contacto</Label>
                  <select value={contact.tipoContacto || 'Comercial'} onChange={(e) => updateContact(index, 'tipoContacto', e.target.value)} className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm">
                    {contactTypeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Correo electrónico</Label>
                  <Input value={contact.correo || ''} onChange={(e) => updateContact(index, 'correo', e.target.value)} placeholder="correo@empresa.com" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Teléfono</Label>
                  <Input value={contact.telefono || ''} onChange={(e) => updateContact(index, 'telefono', e.target.value)} placeholder="Teléfono" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Celular</Label>
                  <Input value={contact.celular || ''} onChange={(e) => updateContact(index, 'celular', e.target.value)} placeholder="Celular" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  } },
  { id: 'correo', label: 'Correo principal', type: 'text', required: false, placeholder: 'correo@empresa.com' },
  { id: 'telefono', label: 'Teléfono principal', type: 'text', required: false, placeholder: '+56 9 1234 5678' },
  { id: 'descripcion', label: 'Descripción', type: 'textarea', required: false, rows: 3, placeholder: 'Observaciones del proveedor' },
  { id: 'estado', label: 'Estado', type: 'select', required: true, defaultValue: 'Activo', options: [{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }] },
];

const formatDateTime = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

export default function SuppliersPage() {
  const toast = useToast();
  const { tenant } = useAuth();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(ITEMS_PER_PAGE);
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ nombre: 1 });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchAndSet = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = {
        ...filters,
        ...(searchTerm ? { $or: [
          { nombre: { $regex: searchTerm, $options: 'i' } },
          { nombreComercial: { $regex: searchTerm, $options: 'i' } },
          { identificacionNumero: { $regex: searchTerm, $options: 'i' } },
          { tipo: { $regex: searchTerm, $options: 'i' } },
        ] } : {}),
      };
      const r = await fetchSuppliers({
        limit: itemsPerPage,
        offset: currentPage,
        query,
        sort,
      });
      setDisplayedData(r?.data || []);
      setTotalItems(r?.totalRecords || 0);
    } catch (e) {
      toast.error('Error al cargar proveedores', e?.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, itemsPerPage, searchTerm, sort, toast]);

  useEffect(() => { fetchAndSet(); }, [fetchAndSet]);

  const filterFields = useMemo(() => [
    { id: 'estado', label: 'Estado', type: 'select', options: [{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }] },
    { id: 'tipo', label: 'Tipo', type: 'select', options: [{ value: 'General', label: 'General' }, { value: 'Especializado', label: 'Especializado' }, { value: 'Estratégico', label: 'Estratégico' }] },
    { id: 'pais', label: 'País', type: 'text' },
    { id: 'ciudad', label: 'Ciudad', type: 'text' },
    { id: 'nombre', label: 'Razón social', type: 'text' },
  ], []);

  const dataColumns = useMemo(() => [
    { id: 'nombre', label: 'Razón social', sortable: true, width: '220px', render: (row) => (
      <div className="flex items-center gap-2 min-w-0">
        <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Handshake className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-foreground truncate">{row.nombre}</div>
          <div className="text-xs text-muted-foreground truncate">{row.nombreComercial || row.contacto || 'Sin nombre comercial'}</div>
        </div>
      </div>
    ) },
    { id: 'identificacion', label: 'Identificación', width: '170px', render: (row) => <span className="text-sm">{row.identificacionTipo ? `${row.identificacionTipo} ${row.identificacionNumero || ''}`.trim() : '—'}</span> },
    { id: 'tipo', label: 'Tipo de proveedor', sortable: true, width: '180px', render: (row) => {
      const types = Array.isArray(row.tiposProveedor) && row.tiposProveedor.length
        ? row.tiposProveedor
        : row.tipo
          ? [row.tipo]
          : [];
      return <span className="text-sm">{types.length ? types.join(', ') : '—'}</span>;
    } },
    { id: 'pais', label: 'País', width: '130px', render: (row) => <span className="text-sm text-muted-foreground">{row.pais || '—'}</span> },
    { id: 'ciudad', label: 'Ciudad', width: '130px', render: (row) => <span className="text-sm text-muted-foreground">{row.ciudad || '—'}</span> },
    { id: 'estado', label: 'Estado', sortable: true, width: '110px', type: 'status' },
  ], []);

  const tableColumns = useMemo(() => [
    ...dataColumns,
    { id: 'actions', key: 'actions', label: 'Acciones', actions: [
      { key: 'edit', icon: 'Edit', tooltip: 'Editar', className: 'hover:bg-foreground hover:text-background' },
      { key: 'toggle', icon: 'Power', tooltip: (r) => (r.estado === 'Activo' ? 'Inactivar' : 'Activar'), className: 'hover:bg-amber-500 hover:text-white' },
      { key: 'delete', icon: 'Trash', tooltip: 'Eliminar', className: 'hover:bg-destructive hover:text-destructive-foreground' },
    ] },
  ], [dataColumns]);

  const handleSort = useCallback((colId) => {
    if (!SORTABLE.has(colId)) return;
    setSort((prev) => {
      const cur = prev[colId];
      if (cur === 1 || cur === 'asc') return { [colId]: -1 };
      if (cur === -1 || cur === 'desc') return { nombre: 1 };
      return { [colId]: 1 };
    });
    setCurrentPage(1);
  }, []);

  const openCreate = () => { setCurrentItem(null); setIsModalOpen(true); };
  const openEdit = (row) => { setCurrentItem(row); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setCurrentItem(null); };

  const handleSave = async (fd) => {
    try {
      const payload = {
        ...fd,
        tipo: Array.isArray(fd.tiposProveedor) && fd.tiposProveedor.length
          ? fd.tiposProveedor[0]
          : fd.tipo || '',
        tiposProveedor: Array.isArray(fd.tiposProveedor) ? fd.tiposProveedor.filter(Boolean) : [],
        contactos: (Array.isArray(fd.contactos) ? fd.contactos : [])
          .filter((contact) => contact && (contact.nombre || contact.correo || contact.telefono || contact.celular))
          .map((contact) => ({
            nombre: contact.nombre || '',
            cargo: contact.cargo || '',
            tipoContacto: contact.tipoContacto || 'Comercial',
            correo: contact.correo || '',
            telefono: contact.telefono || '',
            celular: contact.celular || '',
          })),
      };
      if (payload.contactos.length) payload.contacto = payload.contactos[0].nombre;
      else delete payload.contacto;
      const r = currentItem
        ? await updateSupplier(currentItem._id, payload)
        : await createSupplier(payload);
      if (r?.success) {
        toast.success(currentItem ? 'Proveedor actualizado' : 'Proveedor creado');
        closeModal();
        setCurrentPage(1);
        fetchAndSet();
      } else {
        toast.error('No se pudo guardar', r?.message);
      }
    } catch (e) {
      toast.error('No se pudo guardar', e?.message);
    }
  };

  const handleDeleteConfirmation = useCallback((row) => { setItemToDelete(row); setIsDeleteDialogOpen(true); }, []);
  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      const r = await deleteSupplier(itemToDelete._id);
      if (r?.success) {
        toast.success('Proveedor eliminado', itemToDelete.nombre);
        setIsDeleteDialogOpen(false);
        setItemToDelete(null);
        if (displayedData.length === 1 && currentPage > 1) setCurrentPage((p) => p - 1);
        else fetchAndSet();
      } else {
        toast.error('No se pudo eliminar', r?.message);
      }
    } catch (e) {
      toast.error('No se pudo eliminar', e?.message);
    }
  };

  const handleToggleStatus = useCallback(async (row) => {
    try {
      const r = await toggleSupplierStatus(row._id);
      if (r?.success) {
        const isActive = r.data?.estado === 'Activo';
        toast.success(isActive ? 'Proveedor activado' : 'Proveedor inactivado', row.nombre);
        fetchAndSet();
      } else {
        toast.error('No se pudo cambiar el estado', r?.message);
      }
    } catch (e) {
      toast.error('No se pudo cambiar el estado', e?.message);
    }
  }, [fetchAndSet, toast]);

  const handleTableAction = useCallback((type, row) => {
    if (type === 'edit') return openEdit(row);
    if (type === 'delete') return handleDeleteConfirmation(row);
    if (type === 'toggle') return handleToggleStatus(row);
  }, [handleDeleteConfirmation, handleToggleStatus]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg"><Handshake className="h-8 w-8 text-primary" /></div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">Parametrización de Proveedores</CardTitle>
                {tenant ? (
                  <Badge variant="secondary" className="flex items-center gap-1 font-normal">
                    <Building2 className="h-3 w-3" />{tenant.nombre || tenant.slug}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="flex items-center gap-1 font-normal text-amber-600 border-amber-300">
                    <ShieldAlert className="h-3 w-3" />Modo plataforma
                  </Badge>
                )}
              </div>
              <CardDescription>
                {tenant ? `Catálogo de proveedores del operador ${tenant.nombre || tenant.slug}.` : 'Catálogo general de proveedores para el ERP.'}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <ExportExcelButton service={() => Promise.resolve({ data: displayedData, totalRecords: totalItems })} columns={[]} fileName={`proveedores_${new Date().toISOString().slice(0, 10)}`} />
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={openCreate}><PlusCircle className="mr-2 h-4 w-4" /> Crear proveedor</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                placeholder="Buscar por razón social, nombre comercial, identificación o tipo"
                className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              <span>{totalItems} registros</span>
            </div>
          </div>
          <AppliedFilters filters={filters} fields={filterFields} onRemoveFilter={(k) => setFilters((p) => { const n = { ...p }; delete n[k]; return n; })} onClear={() => { setFilters({}); setCurrentPage(1); }} />
          <DataTable columns={tableColumns} data={displayedData} isLoading={isLoading} onAction={handleTableAction} sort={sort} onSort={handleSort} emptyMessage="Sin proveedores" emptyHint="No hay proveedores que coincidan con los filtros." />
          {!isLoading && totalItems > 0 && (
            <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={setCurrentPage} />
          )}
        </CardContent>
      </Card>

      <FilterDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} fields={filterFields} initialFilters={filters} onApply={(a) => { setFilters(a); setCurrentPage(1); }} />

      <DynamicFormModal isOpen={isModalOpen} onClose={closeModal} onSave={handleSave} fields={formFields} item={currentItem} title={currentItem ? 'Editar proveedor' : 'Crear proveedor'} maxWidth="max-w-2xl" />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proveedor?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && <>Se eliminará <strong>{itemToDelete.nombre}</strong>. Esta acción no se puede deshacer.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
